<script>
/**
 * PatientTasksListener — the new Patient Profile page's Firestore listener on this patient's tasks, with
 * nothing to draw (Patient Profile overhaul, 2026-10-09).
 *
 * WHY IT EXISTS. The page's Tasks tab now draws the Tasks page's own rows (TasksTab.vue, shared with
 * the Schedule View panel), and TasksTab is handed its rows. Something has to own the subscription to
 * the SHARED store slot `state.tasks.tasksTaggedWithPatient` — and reset it on leaving. The classic
 * PatientTasksCard did both while drawing a table; that card is NOT edited (the classic Patient Profile
 * still uses it), so this is its listener half, with the same store actions and the same guards:
 *
 *   subscribe  tasks/getTasksTaggedWithPatient { siteID, patientToLookFor, onError }
 *              • a blank / zero / non-numeric PatNum or no site → no listener, state 'ready' (an empty list)
 *              • a token invalidates a slower, older subscribe: its listener is released, never kept
 *              • the listener erroring (e.g. a denied read on a dead Firebase session) → 'failed',
 *                shown as "Couldn't load tasks", NOT as "No open tasks" (which would be a wrong answer)
 *   teardown   unsubscribe + customer/removeFirestoreListener + tasks/resetTasksTaggedWithPatient —
 *              on a patient change and when the page is destroyed. ONE listener at a time.
 *
 * It is mounted by the PAGE (not by a tab), so switching tabs can never destroy it — the reason the page
 * used to keep PatientTasksCard in a "persistent" tab slot.
 *
 * Emits `state` — 'loading' (subscribing) · 'ready' · 'failed' — on every change.
 *
 * ⛔ No prop named `patNum` or `siteID` (mixin.js computeds shadow them; this component does not use the
 * mixin, but a page-level name that matches its host's would invite the mistake).
 */
export default {
  name: 'PatientTasksListener',

  props: {
    siteId: { type: String, default: '' },
    /** The NUMERIC PatNum — Firestore array-contains on taggedPatients is type-strict. */
    patientNum: { type: [Number, String], default: 0 },
  },

  data() {
    return {
      loaded: false,
      loadFailed: false,
      unsubscribe: null,
      // Bumped on every subscribe() so a stale (slow) dispatch resolution can tell it has been
      // superseded and must not store/keep its listener.
      subscribeToken: 0,
    };
  },

  computed: {
    state() {
      if (this.loadFailed) return 'failed';
      return this.loaded ? 'ready' : 'loading';
    },
    numericPatNum() {
      return Number(this.patientNum);
    },
  },

  watch: {
    state: {
      immediate: true,
      handler(now) {
        this.$emit('state', now);
      },
    },
    patientNum() {
      this.subscribe();
    },
    siteId() {
      this.subscribe();
    },
  },

  mounted() {
    this.subscribe();
  },

  beforeDestroy() {
    this.teardown();
  },

  methods: {
    async subscribe() {
      this.teardown();
      // Invalidate any in-flight subscribe BEFORE the await, valid or not.
      const token = ++this.subscribeToken;
      const n = this.numericPatNum;
      // `Number('')`, `Number(null)`, `Number('  ')` are all 0 and finite — a blank or zero/negative
      // PatNum is not a real patient, so require n > 0.
      if (!Number.isFinite(n) || n <= 0 || !this.siteId) {
        this.loaded = true;
        return;
      }
      const unsub = await this.$store.dispatch('tasks/getTasksTaggedWithPatient', {
        siteID: this.siteId,
        patientToLookFor: n,
        // Only the CURRENT subscription may flip the tab to "couldn't load".
        onError: () => {
          if (token === this.subscribeToken) this.loadFailed = true;
        },
      });
      // A newer subscribe() started while we awaited — this resolution is stale. Never store it or
      // leave its listener live.
      if (token !== this.subscribeToken) {
        if (typeof unsub === 'function') {
          unsub();
          this.$store.commit('customer/removeFirestoreListener', unsub);
        }
        return;
      }
      this.unsubscribe = unsub;
      this.loaded = true;
    },

    teardown() {
      if (typeof this.unsubscribe === 'function') {
        this.unsubscribe();
        this.$store.commit('customer/removeFirestoreListener', this.unsubscribe);
      }
      this.unsubscribe = null;
      this.$store.commit('tasks/resetTasksTaggedWithPatient');
      this.loaded = false;
      this.loadFailed = false;
    },
  },

  // Renders nothing.
  render() {
    return null;
  },
};
</script>
